import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import Cookies from 'js-cookie';
import config from '../config';
import { decrypt, encrypt } from '../utils/encryption';
import api from '../services/api';
import ContextPlan from '../models/Plan/ContextPlan';

interface AuthorityListItem {
  authority_id?: number
  authority_name?: string
  authority_feature: string
  comment?: string
}

interface Authority {
  authority_group_id?: number
  authority_group_name?: string
  comment?: string
  authorityList: AuthorityListItem[]
}

interface ResponseUser {
  data: {
    user: User
    token: string
    user_roles: UserRoles
    permissions: string[]
    authority: Authority[]
    webdb_key: string
    webdb_pass?: string
  }
}

interface LoginUser {
  id: string,
  password: string,
  remember: boolean
}

interface User {
  id: number
  name: string
  surname: string
  myPlan: ContextPlan
  is_invoice_mode_enabled: boolean
  has_pendences: boolean
  mail: string
  login_id: string
  type: number
  user_id: number
  administrator_certification: boolean
  veterinarian: {
    veterinarian_id: number
    veterinarian_name: string
    user_id: number
  }[]
}

export enum UserRole {
  MANAGER = 'manager',
  USER = 'user',
}

export type UserRoles = UserRole.MANAGER | UserRole.USER | null;

// TODO: seperate into UsersContext and AuthContext
interface UserContextInterface {
  isAuthenticated: boolean,
  user: Partial<User>,
  loginUser: (data: LoginUser) => void,
  logoutUser: () => void,
  validationErrors: string[],
  loading: boolean,
  errorMessage: string,
  authority: Authority[]
  userCan: (feature: string) => boolean
  userRoles: UserRoles,
  updateUserContext: (data: any) => void,
  updatePlanInfo: (plan: ContextPlan) => void,
}

const UserContext = createContext<UserContextInterface>({} as UserContextInterface);
UserContext.displayName = 'UserContext';

interface Props {
  children: JSX.Element
}

interface UserIdentity { user: User; authorities: Authority[]; token: string; userRoles: UserRoles }

interface UserContextCookieValue {
  token: string
  user: {
    name: string
  }
  authorities: Authority[]
  userRoles: UserRoles
}

export const UserProvider: React.FC<Props> = ({ children }) => {
  const [user, setUser] = useState<Partial<User>>();
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRoles>(null);

  const userCan = useCallback((feature: string) => {
    if (authorities == null) {
      return false;
    }

    return authorities.some((item) => item.authorityList.some((authority) => authority.authority_feature === feature));
  }, [authorities]);

  const getUserFromToken = useCallback(() => {
    const cookiesList = Cookies.get();
    const decryptedValue = cookiesList[config.auth.cookieName];

    if (!decryptedValue) {
      return null;
    }

    const decrypted = decrypt(decryptedValue);

    let userIdentity: UserIdentity;
    try {
      userIdentity = JSON.parse(decrypted) as UserIdentity;
    } catch (e) {
      console.error(e);
    }

    return userIdentity;
  }, []);

  const setNewCookie = useCallback((data: any) => {
    const cookiesList = Cookies.get();
    const cookiesListDecrypted = decrypt(cookiesList[config.auth.cookieName]);
    const cookieParsed = JSON.parse(cookiesListDecrypted);
    cookieParsed.user = data;
    const cookieValue = encrypt(JSON.stringify(cookieParsed));
    Cookies.set(config.auth.cookieName, cookieValue);
  }, []);

  const updateUserContext = useCallback((data: any) => {
    const userIdentity = getUserFromToken();

    userIdentity.user = {
      ...userIdentity.user,
      ...data,
    };

    const authUser = userIdentity.user;
    setNewCookie(data);
    setUser(authUser);
    setAuthorities(userIdentity.authorities);
    setUserRoles(userIdentity.userRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePlanInfo = useCallback(async (plan: ContextPlan) => {
    const userIdentity = getUserFromToken();
    userIdentity.user.myPlan = plan;
    const authUser = userIdentity.user;

    setNewCookie(authUser);
    setUser(authUser);
    setAuthorities(userIdentity.authorities);
    setUserRoles(userIdentity.userRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const userIdentity = getUserFromToken();
    if (!userIdentity) {
      setIsAuthenticated(false);
      return;
    }

    setIsAuthenticated(true);
    const authUser = userIdentity.user;
    setUser(authUser);
    setAuthorities(userIdentity.authorities);
    setUserRoles(userIdentity.userRoles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginUser = useCallback(async ({ id, password, remember }: LoginUser) => {
    setLoading(true);
    setValidationErrors([]);
    setErrorMessage('');

    await api.post<User, ResponseUser>('/auth/login', {
      mail: id,
      password,
    }).then((response) => {
      const {
        token,
        user_roles,
        user,
        webdb_key,
      } = response.data;

      if (user_roles !== UserRole.USER) {
        setErrorMessage('権限はありません');
        return;
      }

      const authorityLogin: Authority = {
        authorityList: [{
          authority_feature: 'user_logged',
        }],
      };

      const authorities = [authorityLogin];
      const { cookieName } = config.auth;

      const cookieRawValue: UserContextCookieValue = {
        token,
        user,
        authorities,
        userRoles: user_roles,
      };

      const cookieValue = encrypt(JSON.stringify(cookieRawValue));

      Cookies.set(cookieName, cookieValue, {
        secure: config.env === 'production',
        expires: remember ? 365 : null, // if the user didn't select remember me, cookie expires on session end
      });

      if (webdb_key && webdb_key !== '') {
        localStorage.setItem(config.api.webdbKeyStorage, webdb_key);
        location.href = '/';
      } else {
        setErrorMessage('some error');
      }

      setIsAuthenticated(true);
    }).catch((error) => {
      // TODO: サーバー側のレスポンスと擦り合わせる
      if (error?.response?.data?.error) {
        setValidationErrors(error.response.data.error);
        return;
      }

      if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        return;
      }

      setErrorMessage('some error');
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const logoutUser = () => {
    setIsAuthenticated(false);

    api.post<User, ResponseUser>('/auth/logout', {})
      .then(() => {
        localStorage.clear();
        Cookies.remove(config.auth.cookieName);
        location.href = '/login';
      });
  };

  const value = useMemo(
    () => ({
      userCan,
      authority: authorities,
      isAuthenticated,
      user,
      loginUser,
      logoutUser,
      validationErrors,
      loading,
      errorMessage,
      userRoles,
      updateUserContext,
      updatePlanInfo,
    }),
    [userCan, authorities, isAuthenticated, user, loginUser, validationErrors, loading, errorMessage, userRoles, updateUserContext, updatePlanInfo],
  );

  return (
    <UserContext.Provider
      value={value}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextInterface => {
  const {
    userCan,
    authority,
    user,
    logoutUser,
    loginUser,
    isAuthenticated,
    validationErrors,
    loading,
    errorMessage,
    userRoles,
    updateUserContext,
    updatePlanInfo,
  } = useContext(UserContext);

  return {
    userCan,
    authority,
    isAuthenticated,
    user,
    loginUser,
    logoutUser,
    validationErrors,
    loading,
    errorMessage,
    userRoles,
    updateUserContext,
    updatePlanInfo,
  };
};
